/**
 * TanStack Query wiring (ARCHITECTURE.md §8).
 *
 * Server state lives entirely in Query; there is no global store. Route loaders
 * call `queryClient.ensureQueryData(...)` to prefetch, and components read the
 * same keys with `useQuery`. A single `QueryCache.onError` hook watches for lost
 * sessions (`401`) and hands control to the router-supplied `onUnauthorized`
 * callback so the app redirects to `/login` from anywhere.
 */
import { QueryClient, QueryCache, queryOptions } from "@tanstack/react-query";
import type { AuthSession, GetSettingsResponse } from "@richrich/contracts";
import { api, unwrap, isUnauthorized, isUnclaimed } from "./api";
import { keys } from "./queryKeys";

/**
 * Redirect-to-login callback, installed by `main.tsx` once the router exists.
 * Kept as an injected function to avoid a `query ⇄ router` import cycle.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/**
 * Current auth/claim state. `/api/auth/session` returns the `{ claimed,
 * authenticated, user }` view when the instance is claimed; before that the
 * pre-claim gate (§7) answers `409 UNCLAIMED`, which we normalize to a synthetic
 * unclaimed session so the login page can render the claim flow.
 */
export const sessionQuery = queryOptions({
  queryKey: keys.auth.session(),
  queryFn: async (): Promise<AuthSession> => {
    try {
      return await unwrap<AuthSession>(await api.auth.session.$get());
    } catch (error) {
      if (isUnclaimed(error)) {
        return { claimed: false, authenticated: false, user: null };
      }
      throw error;
    }
  },
  staleTime: 30_000,
  retry: false,
});

/** Settings + derived feature gates (`{ ai, toss, push }`) — drives nav gating. */
export const settingsQuery = queryOptions({
  queryKey: keys.settings.root(),
  queryFn: async (): Promise<GetSettingsResponse> =>
    unwrap<GetSettingsResponse>(await api.settings.$get()),
  staleTime: 60_000,
  retry: false,
});

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isUnauthorized(error)) {
        onUnauthorized?.();
      }
    },
  }),
  defaultOptions: {
    queries: {
      // A single-user dashboard: don't hammer the Worker on window focus.
      refetchOnWindowFocus: false,
      staleTime: 15_000,
      retry: (failureCount, error) => !isUnauthorized(error) && failureCount < 2,
    },
  },
});
